import React, { useState, useContext } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OperationContext from '../context';
import { TimelineStage, TimelineMilestone } from '../../../../services/types/Timeline';
import { Button, Modal } from '../../../../shared/components';
import {
  customDateStr,
  isDraft,
  tooltipPreventOverflowModifiers,
} from '../../../../shared/misc/utils';
import MilestoneStatusBadge from './MilestoneStatusBadge';
import MilestoneDateEditable from './MilestoneDateEditable';
import MainContext from '../../../App/MainProvider/context';
import UserMilestonesEditHistory from './UserMilestonesEditHistory';

interface MilestoneRowData {
  milestone: TimelineMilestone;
  saveDate: (milestoneId: number, date: Date | null) => void;
}

const MilestoneRow = (props: MilestoneRowData) => {
  const { milestone, saveDate } = props;
  const { operation } = useContext(OperationContext);
  const { inSuperUserMode } = useContext(MainContext);
  const [editing, setEditing] = useState(false);
  const { t } = useTranslation();
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const isEditableMilestone = milestone.editable;
  const canEdit =
    isEditableMilestone && (operation.isMember || inSuperUserMode()) && !isDraft(operation);

  const isLegacy = milestone.name === 'Toolkit' && milestone.convergenceDate !== null;
  const compassDate = milestone.compassDate ? customDateStr(milestone.compassDate) : 'N/A';

  return (
    <tr>
      <td className="title">{milestone.name}</td>
      <td>
        {(isLegacy || !milestone.isMission) && customDateStr(milestone.convergenceDate)}

        {isLegacy && (
          <span className="deleted-toolkit deleted-toolkit-text">
            <span className="ml-2 toolkit-tooltip" id={`tr-toolkit-id-${milestone.id}`}>
              <FontAwesomeIcon icon="exclamation-circle" className="lastUpdateInfo" />
            </span>
            <UncontrolledTooltip
              className="tooltip-toolkit"
              placement="bottom"
              target={`tr-toolkit-id-${milestone.id}`}
              modifiers={tooltipPreventOverflowModifiers}
            >
              {t('toolkitMigratedMilestoneMsg')}
            </UncontrolledTooltip>
          </span>
        )}
      </td>
      <td>
        {canEdit ? (
          <MilestoneDateEditable
            milestone={milestone}
            isEditing={editing}
            saveDate={saveDate}
            toggleEdition={() => setEditing((prev) => !prev)}
            onSnake={false}
          />
        ) : (
          <span className="date">{!isLegacy && compassDate}</span>
        )}
      </td>
      <td>{milestone.isMission && customDateStr(milestone.startDate)}</td>
      <td>{milestone.isMission && customDateStr(milestone.endDate)}</td>
      <td>
        <MilestoneStatusBadge code={milestone.status} />
      </td>

      <td>
        {isEditableMilestone ? (
          <Button
            htmlId={`esg_milestone_button_${milestone.id}`}
            className="btn-tertiary no-padding"
            icon="clipboard-list"
            text=""
            onClick={() => setShowHistoryModal(true)}
            title={t('history')}
          />
        ) : null}
      </td>
      {/* Modal.tsx siempre renderiza un <div> propio en la raíz (visible o
          no) - como sibling directo de los <td> de arriba, dentro de un
          <tr>, es HTML inválido ("<tr> cannot contain a nested <div>",
          encontrado en QA manual). Un <td> oculto lo hace válido sin cambiar
          nada visualmente: el modal en sí lo abre/cierra reactstrap con su
          propio overlay, independiente de dónde viva este <div> wrapper. */}
      <td style={{ display: 'none' }}>
        <Modal
          title={`${milestone.name} ${t('history')}`}
          visible={showHistoryModal}
          setVisible={() => setShowHistoryModal(false)}
          actions={[{ text: 'Close', action: () => setShowHistoryModal(false) }]}
          customClass="wide-modal"
          onClosed={() => setShowHistoryModal(false)}
        >
          <UserMilestonesEditHistory
            operationId={operation.id}
            milestoneId={milestone.milestoneId}
          />
        </Modal>
      </td>
    </tr>
  );
};

interface StageRow {
  stage: TimelineStage;
  saveDate: (milestoneId: number, date: Date | null) => void;
}

const TimelineListRow = (props: StageRow) => {
  const { t } = useTranslation();

  const { stage, saveDate } = props;
  const [rowExpanded, setRowExpanded] = useState(stage.current);

  const chevron = rowExpanded ? 'chevron-down' : 'chevron-right';

  const renderMilestones = (milestones: TimelineMilestone[]) =>
    milestones.map((x, i) => (
      <MilestoneRow key={`milestoneRow${i.toString()}`} milestone={x} saveDate={saveDate} />
    ));

  return (
    <div className={`timeline-list-row ${rowExpanded ? 'active' : ''}`}>
      <Button
        className="btn-tertiary py-0 px-1"
        icon={chevron}
        text={stage.name}
        type="button"
        onClick={() => setRowExpanded((current) => !current)}
      />
      {rowExpanded && (
        <div className="table-wrapper">
          <Table responsive className="table--bordered">
            <thead>
              <tr>
                <th>{t('description')}</th>
                <th>{t('convergenceDate')}</th>
                <th>{t('compassDate')}</th>
                <th>{t('startDate')}</th>
                <th>{t('endDate')}</th>
                <th>{t('status')}</th>
                <th>{t('history')}</th>
              </tr>
            </thead>
            <tbody>{renderMilestones(stage.milestones)}</tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

interface Props {
  stages: TimelineStage[];
  saveDate: (milestoneId: number, date: Date | null) => void;
}

const TimelineList = (props: Props) => {
  const { stages, saveDate } = props;

  const renderStages = () =>
    stages.map((x, i) => (
      <TimelineListRow key={`stageRow${i.toString()}`} stage={x} saveDate={saveDate} />
    ));

  return <div className="timeline-list">{renderStages()}</div>;
};

export default TimelineList;
